import * as THREE from 'three';
import { ATMO, WIND } from '../config/tuning';
import { Rng } from '../core/Rng';

/**
 * Air density, wind, and turbulence (SPEC §3.2.5).
 *
 * Two distinct effects, and the distinction matters for how the game feels:
 *  - **Gusts** move the air mass, changing relative airflow and therefore the forces.
 *  - **Turbulence** perturbs *angular velocity*, not position. Shaking the camera by displacing
 *    the aircraft reads as noise applied to the image; rocking it about its axes reads as air.
 *
 * Both are driven by seeded value noise, so a replay reproduces the same gust at the same tick.
 *
 * Units: kg/m³, m/s, rad/s², metres, seconds.
 */

const NOISE_TABLE_SIZE = 256;
const NOISE_TABLE_MASK = NOISE_TABLE_SIZE - 1;

/**
 * 1-D seeded value noise with smoothstep interpolation, one independent channel per axis.
 * Cheap, allocation-free, and deterministic — which is all turbulence needs to be.
 */
class ValueNoise {
  private readonly table: Float32Array;

  constructor(seed: number) {
    const rng = new Rng(seed);
    this.table = new Float32Array(NOISE_TABLE_SIZE);
    for (let i = 0; i < NOISE_TABLE_SIZE; i += 1) this.table[i] = rng.range(-1, 1);
  }

  /** Sample channel `channel` at position `x` (noise units). Returns roughly [-1, 1]. */
  sample(x: number, channel: number): number {
    const cell = Math.floor(x);
    const fraction = x - cell;
    const a = this.valueAt(cell, channel);
    const b = this.valueAt(cell + 1, channel);
    const t = fraction * fraction * (3 - 2 * fraction);
    return a + (b - a) * t;
  }

  private valueAt(cell: number, channel: number): number {
    const hashed = Math.imul(cell ^ Math.imul(channel + 1, 0x9e3779b1), 0x85ebca6b);
    return this.table[(hashed >>> 13) & NOISE_TABLE_MASK];
  }
}

export class Atmosphere {
  /** Constant wind vector for the mission, world m/s. */
  readonly windMs = new THREE.Vector3();

  /** Gust and turbulence intensity multiplier, 0 disables both. */
  gustScale = 1;
  turbulenceScale = 1;

  private readonly noise: ValueNoise;

  constructor(seed: number, windMs?: THREE.Vector3) {
    this.noise = new ValueNoise(seed);
    if (windMs !== undefined) {
      this.windMs.copy(windMs);
    } else {
      const [x, y, z] = WIND.defaultMs;
      this.windMs.set(x, y, z);
    }
  }

  /** Still air: no wind, no gusts, no turbulence. Used by tests that measure energy. */
  static still(seed: number): Atmosphere {
    const atmosphere = new Atmosphere(seed, new THREE.Vector3(0, 0, 0));
    atmosphere.gustScale = 0;
    atmosphere.turbulenceScale = 0;
    return atmosphere;
  }

  /** Air density at an altitude, kg/m³. Exponential approximation to the standard atmosphere. */
  densityAtM(altitudeM: number): number {
    return ATMO.RHO_0 * Math.exp(-Math.max(0, altitudeM) / ATMO.SCALE_HEIGHT_M);
  }

  /**
   * Total wind at a point in time — constant mission wind plus a low-amplitude gust.
   * Writes world m/s into `out`.
   */
  sampleWind(altitudeM: number, simTimeS: number, out: THREE.Vector3): THREE.Vector3 {
    out.copy(this.windMs);
    if (this.gustScale === 0) return out;

    const intensity = this.gustIntensity(altitudeM) * this.gustScale;
    if (intensity === 0) return out;

    const phase = simTimeS * WIND.gustFrequencyHz;
    const amplitude = WIND.gustAmplitudeMs * intensity;
    out.x += this.noise.sample(phase, 0) * amplitude;
    out.y += this.noise.sample(phase, 1) * amplitude * 0.5;
    out.z += this.noise.sample(phase, 2) * amplitude;
    return out;
  }

  /**
   * Turbulence as an angular acceleration perturbation, body-frame (p, q, r) rad/s².
   * Writes into `out`.
   */
  sampleTurbulence(altitudeM: number, simTimeS: number, out: THREE.Vector3): THREE.Vector3 {
    out.set(0, 0, 0);
    if (this.turbulenceScale === 0) return out;

    const intensity =
      this.gustIntensity(altitudeM) *
      this.turbulenceScale *
      ATMO.TURB_SCALE *
      WIND.turbulenceAmplitudeRadS2;
    if (intensity === 0) return out;

    const basePhase = simTimeS * ATMO.TURB_FREQ;
    const octavePhase = basePhase * WIND.turbulenceOctaveFrequency;
    const octaveGain = WIND.turbulenceOctaveGain;

    out.x = (this.noise.sample(basePhase, 3) + this.noise.sample(octavePhase, 6) * octaveGain) * intensity;
    out.y = (this.noise.sample(basePhase, 4) + this.noise.sample(octavePhase, 7) * octaveGain) * intensity;
    out.z = (this.noise.sample(basePhase, 5) + this.noise.sample(octavePhase, 8) * octaveGain) * intensity;
    return out;
  }

  /**
   * Gust/turbulence intensity by altitude: strongest in the boundary layer near the surface,
   * falling away with height. Unitless, in [0, 1].
   */
  gustIntensity(altitudeM: number): number {
    const height = Math.max(0, altitudeM);
    if (height >= ATMO.GROUND_TURB_ALT_M) {
      // Above the boundary layer a residual remains, decaying with the density scale height.
      return 0.35 * Math.exp(-(height - ATMO.GROUND_TURB_ALT_M) / ATMO.SCALE_HEIGHT_M);
    }
    const nearGround = 1 - height / ATMO.GROUND_TURB_ALT_M;
    return 0.35 + 0.65 * nearGround;
  }
}
